import { Stack } from '@mantine/core';
import {
	useApiGetAutomationMetrics,
	useApiGetAutomationTimeseries,
	type ApiGetAutomationTimeseriesVariables,
} from '@repo/api-codegen';
import { keepPreviousData } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { MetricsAutomationCards } from './components/cards/MetricsAutomationCards';
import { AutomationsOverviewChart } from './components/charts/AutomationsOverviewChart';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';

import { isValidTimeRange, isValidViewOption } from './types.ts';
export const AutomationsOverviewTabController = observer(() => {
	const {
		timeRangeValue,
		setTimeRangeValue,
		dateRangeOption,
		setDateRangeOption,
		dateFilter,
		setDateFilter,
	} = useOverviewMetricDatetime({ cacheKey: 'automations' });

	// Create wrapper functions to handle type conversion with validation
	const handleSetDateFilter = useCallback(
		(value: string) => {
			if (isValidTimeRange(value)) {
				setDateFilter(value);
			}
		},
		[setDateFilter]
	);

	const handleSetDateRangeOption = useCallback(
		(value: string) => {
			if (isValidViewOption(value)) {
				setDateRangeOption(value);
			}
		},
		[setDateRangeOption]
	);

	const { data: metrics, isPending: isMetricsLoading } =
		useApiGetAutomationMetrics(
			{
				queryParams: {
					time_range: dateFilter,
				},
			},
			{ placeholderData: keepPreviousData }
		);
	const { data: timeseries, isPending: isTimeseriesLoading } =
		useApiGetAutomationTimeseries(
			{
				queryParams: {
					time_range: dateFilter,
					view_option: dateRangeOption,
				},
			} as ApiGetAutomationTimeseriesVariables,
			{ placeholderData: keepPreviousData }
		);

	if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
		return <MetricsOverviewTabSkeleton cards={2} />;
	}

	return (
		<Stack spacing={0} pt="md">
			<MetricsAutomationCards automationMetrics={metrics} />
			<AutomationsOverviewChart
				timeseries={timeseries}
				dateFilter={dateFilter || '24h'}
				setDateFilter={handleSetDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={dateRangeOption || 'day'}
				setDateRangeOption={handleSetDateRangeOption}
			/>
		</Stack>
	);
});
